/**
 * Competition List Page
 * 
 * Displays all available competitions
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNavbar from '../components/AppNavbar';
import CompetitionCard from '../components/CompetitionCard';
import { useAuth } from '../context/AuthContext';
import PermissionGuard from '../components/PermissionGuard';
import competitionService from '../services/competitionService';
import './CompetitionListPage.css';

const PAGE_SIZE = 9;

const CompetitionListPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin, logout, isAuthenticated } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, completed
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [paginationError, setPaginationError] = useState(null);
  const observerRef = useRef(null);
  const nextOffsetRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(true);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  const loadCompetitions = useCallback(async (options = {}) => {
    const { reset = false } = options;

    try {
      const nextOffset = reset ? 0 : nextOffsetRef.current;

      if (reset) {
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        setPaginationError(null);
        setHasMore(true);
        hasMoreRef.current = true;
      } else {
        if (loadingMoreRef.current || loadingRef.current || !hasMoreRef.current) {
          return;
        }

        loadingMoreRef.current = true;
        setLoadingMore(true);
        setPaginationError(null);
      }
      
      const filters = {};
      if (filter !== 'all') {
        filters.status = filter;
      }

      filters.limit = PAGE_SIZE;
      filters.offset = nextOffset;
      
      const data = await competitionService.getAll(filters);
      const nextCompetitions = Array.isArray(data.competitions) ? data.competitions : Array.isArray(data) ? data : [];

      setCompetitions((currentCompetitions) => {
        if (reset) {
          return nextCompetitions;
        }

        const seenCompetitionIds = new Set(currentCompetitions.map((competition) => competition.id));
        const uniqueNewCompetitions = nextCompetitions.filter((competition) => !seenCompetitionIds.has(competition.id));
        return [...currentCompetitions, ...uniqueNewCompetitions];
      });

      nextOffsetRef.current = nextOffset + nextCompetitions.length;

      if (typeof data.hasMore === 'boolean') {
        setHasMore(data.hasMore);
        hasMoreRef.current = data.hasMore;
      } else {
        const nextHasMore = nextCompetitions.length === PAGE_SIZE;
        setHasMore(nextHasMore);
        hasMoreRef.current = nextHasMore;
      }
    } catch (err) {
      console.error('Failed to load competitions:', err);
      if (reset) {
        setError('Failed to load competitions. Please try again.');
      } else {
        setPaginationError('Unable to load more competitions right now.');
      }
    } finally {
      if (reset) {
        loadingRef.current = false;
        setLoading(false);
      } else {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [filter]);

  useEffect(() => {
    nextOffsetRef.current = 0;
    hasMoreRef.current = true;
    loadingMoreRef.current = false;
    setCompetitions([]);
    setHasMore(true);
    setPaginationError(null);
    loadCompetitions({ reset: true });
  }, [filter, loadCompetitions]);

  const lastCompetitionElementRef = useCallback((node) => {
    if (loadingRef.current || loadingMoreRef.current) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMoreRef.current && !error) {
        loadCompetitions({ reset: false });
      }
    }, {
      rootMargin: '200px'
    });

    if (node) {
      observerRef.current.observe(node);
    }
  }, [error, loadCompetitions]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleCompetitionClick = (competitionId) => {
    navigate(`/competitions/${competitionId}`);
  };

  const handleCreateCompetition = () => {
    navigate('/competitions/new');
  };

  const handleEditCompetition = (competitionId) => {
    navigate(`/competitions/${competitionId}/edit`);
  };

  const handleViewSubmissions = (competitionId) => {
    navigate(`/competitions/${competitionId}/submissions`);
  };

  const handleParticipateNow = (competitionId) => {
    navigate(`/competitions/${competitionId}?openSubmit=1`);
  };

  const handleViewMediaSources = (competitionId) => {
    navigate(`/reels/${competitionId}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigationItems = isAuthenticated
    ? [
        { label: 'Home', to: '/', isActive: false },
        { label: 'Dashboard', to: '/dashboard', isActive: false },
        { label: 'Competitions', to: '/competitions', isActive: true }
      ]
    : [
        { label: 'Home', to: '/', isActive: false },
        { label: 'Competitions', to: '/competitions', isActive: true },
        { label: 'Login', to: '/login', isActive: false }
      ];

  return (
    <div className="competition-list-page">
      <AppNavbar
        user={user}
        onLogout={isAuthenticated ? handleLogout : undefined}
        navigationItems={navigationItems}
        brandTo="/"
        showUserSection={isAuthenticated}
      />

      {/* Page Content */}
      <div className="competition-content">
        {/* Header */}
        <div className="competition-header">
          <div>
            <h1>Competitions</h1>
            <p>Participate in exciting video challenges and win prizes</p>
          </div>
          
          <PermissionGuard permission="CREATE_COMPETITION">
            <button onClick={handleCreateCompetition} className="create-button">
              + Create Competition
            </button>
          </PermissionGuard>
        </div>

        {/* Filters */}
        <div className="filter-bar">
          <button
            className={`filter-button ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-button ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button
            className={`filter-button ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading competitions...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={loadCompetitions} className="retry-button">
              Retry
            </button>
          </div>
        )}

        {/* Competition Grid */}
        {!loading && !error && (
          <>
            {competitions.length === 0 ? (
              <div className="empty-state">
                <h3>No competitions found</h3>
                <p>Check back later for new competitions!</p>
              </div>
            ) : (
              <div className="competition-grid">
                {competitions.map((competition, index) => {
                  const isLastCompetition = index === competitions.length - 1;

                  return (
                    <div key={competition.id} ref={isLastCompetition ? lastCompetitionElementRef : null}>
                      <CompetitionCard
                        competition={competition}
                        onClick={handleCompetitionClick}
                        onEdit={handleEditCompetition}
                        onParticipate={(competitionId) => {
                          if (!isAuthenticated) {
                            navigate('/login');
                            return;
                          }

                          handleParticipateNow(competitionId);
                        }}
                        onViewMediaSources={handleViewMediaSources}
                        onViewSubmissions={handleViewSubmissions}
                        canEdit={canEditCompetition(competition, user, isAdmin)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {paginationError && (
              <div className="pagination-feedback error">
                <p>{paginationError}</p>
                <button onClick={() => loadCompetitions({ reset: false })} className="retry-button">
                  Retry Loading More
                </button>
              </div>
            )}

            {loadingMore && (
              <div className="pagination-feedback">
                <div className="spinner small"></div>
                <p>Loading more competitions...</p>
              </div>
            )}

            {!loadingMore && competitions.length > 0 && !hasMore && (
              <div className="pagination-feedback complete">
                <p>You've reached the end of the competition list.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function canEditCompetition(competition, user, isAdmin) {
  if (!competition || !user) {
    return false;
  }

  if (competition.createdBy === user.id) {
    return true;
  }

  return typeof isAdmin === 'function' ? isAdmin() : false;
}

export default CompetitionListPage;
